import { CheckOutlined, CopyOutlined } from '@ant-design/icons';
//import { ColorChooser, ImageLoader, MessageDisplay } from 'components/common';
import { ProductShowcaseGrid } from 'components/product';
import { RECOMMENDED_PRODUCTS, SHOP } from 'constants/routes';
import {
  ALL_IN_ONE_PLAN,
  PAYMENT_PLAN,
  getPaymentProvider,
  getUserPlan
} from 'constants/plans';
import { displayMoney } from 'helpers/utils';
import { TypeAnimation } from 'react-type-animation';
import { SocialIcon } from 'react-social-icons';
import VCardFileCreator from './VCardFileCreator';
import { Preloader } from 'components/common';

import {
  useBasket,
  useDocumentTitle,
  useProduct,
  useRecommendedProducts,
  useScrollTop,
  useUser, useRprod
} from 'hooks';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import firebase from 'services/firebase';
import Select from 'react-select';
import {
  isWhatsAppLink,
  normalizeProfileLink,
  normalizeWhatsAppLink
} from 'utils/profileLinks';
import logoos from 'images/Loggo.png';
import locationPng from 'images/map.png';
import adobePDFPNG from 'images/apdf.png';

import websitePng from 'images/website.png';

import './styles.css'
import './sty.css'

const PROFILE_REDIRECTS = {
  sushianasocial: 'https://linktr.ee/sushi.ana?utm_source=qr_code'
};

const createAnalyticsId = () => (
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
);

const getStoredAnalyticsId = (storage, key) => {
  try {
    const storedId = storage.getItem(key);
    if (storedId) return storedId;
    const id = createAnalyticsId();
    storage.setItem(key, id);
    return id;
  } catch (error) {
    return createAnalyticsId();
  }
};

const getDeviceType = () => {
  const width = window.innerWidth;
  if (width < 768) return 'Mobile';
  if (width < 1024) return 'Tablet';
  return 'Desktop';
};

const getReferrerHost = () => {
  if (!document.referrer) return '';
  try {
    return new URL(document.referrer).hostname.slice(0, 200);
  } catch (error) {
    return '';
  }
};

const getLocalDay = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isNumericPaymentLabel = (label) => {
  const value = String(label || '').trim();
  return /\d/.test(value) && /^\+?[\d\s().-]+$/.test(value);
};

const copyTextToClipboard = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

const ViewUser = () => {
  const { id } = useParams();
  const redirectUrl = PROFILE_REDIRECTS[String(id || '').toLowerCase()];

  const { rprod } = useRprod(id);


  const { user, isLoading, error } = useUser(rprod.user);


  const { addToBasket, isItemOnBasket } = useBasket(id);
  useScrollTop();
  useDocumentTitle(`${user?.fullname || 'User'} | Takka`);

  const [selectedImage, setSelectedImage] = useState(user?.banner || '');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [copiedPaymentLink, setCopiedPaymentLink] = useState('');

  const {
    recommendedProducts,
    fetchRecommendedProducts,
    isLoading: isLoadingFeatured,
    error: errorFeatured
  } = useRecommendedProducts(6);
  const colorOverlay = useRef(null);
  const trackedProfile = useRef('');

  useEffect(() => {
    window.location.reload
    setSelectedImage(user?.banner);
  }, [user]);

  useEffect(() => {
    const userId = rprod && rprod.user;
    const trackingKey = `${userId || ''}:${id || ''}`;
    if (redirectUrl || !user || !userId || !id || trackedProfile.current === trackingKey) return;

    trackedProfile.current = trackingKey;
    firebase.trackProfileView({
      userId,
      profileId: id,
      visitorId: getStoredAnalyticsId(window.localStorage, 'takkaAnalyticsVisitor'),
      sessionId: getStoredAnalyticsId(window.sessionStorage, 'takkaAnalyticsSession'),
      day: getLocalDay(),
      referrerHost: getReferrerHost(),
      deviceType: getDeviceType()
    }).catch(() => {
      // Analytics must never interrupt or hide a public profile.
    });
  }, [id, redirectUrl, rprod, user]);

  useEffect(() => {
    if (redirectUrl) window.location.replace(redirectUrl);
  }, [redirectUrl]);

  if (redirectUrl) {
    return (
      <main className="profile-redirecting">
        <Preloader />
        <p>Redirecting...</p>
      </main>
    );
  }

  const onSelectedSizeChange = (newValue) => {
    setSelectedSize(newValue.value);
  };

  const onSelectedColorChange = (color) => {
    setSelectedColor(color);
    if (colorOverlay.current) {
      colorOverlay.current.value = color;
    }
  };

  const handleAddToBasket = () => {
    addToBasket({ ...user, selectedColor, selectedSize: selectedSize || product.sizes[0] });
  };

  const handleCopyPaymentNumber = async (number, key) => {
    try {
      await copyTextToClipboard(number.trim());
      setCopiedPaymentLink(key);
      window.setTimeout(() => setCopiedPaymentLink(''), 2000);
    } catch (copyError) {
      // Keep the payment link usable if clipboard access is unavailable.
    }
  };

  const plan = getUserPlan(user?.plan);
  const isAllInOne = plan === ALL_IN_ONE_PLAN;
  const isPayment = plan === PAYMENT_PLAN;
  const socialMediaLabelClassName = isAllInOne
    ? 'social-media-label social-media-label-all-in-one'
    : 'social-media-label';

  const socialLinks = isPayment ? [] : [
    ...(user?.whatsapp ? [{
      url: normalizeWhatsAppLink(user.whatsapp),
      label: user.whatsappLabel || 'WhatsApp',
      network: 'whatsapp'
    }] : []),
    ...(user?.friends || [])
      .filter((account) => account && account.name)
      .map((account) => {
        const url = normalizeProfileLink(account.name);
        return {
          url,
          label: account.label || '',
          icon: isAllInOne ? account.icon || '' : '',
          network: isWhatsAppLink(url) ? 'whatsapp' : ''
        };
      }),
    ...(user?.website ? [{
      url: normalizeProfileLink(user.website),
      label: 'Website',
      icon: websitePng
    }] : []),
    ...(user?.gMaps ? [{
      url: normalizeProfileLink(user.gMaps),
      label: 'Google Maps',
      icon: locationPng
    }] : [])
  ];
  const paymentLinks = (user?.paymentLinks || [])
    .filter((paymentLink) => paymentLink && paymentLink.url)
    .map((paymentLink) => ({
      ...paymentLink,
      providerDetails: getPaymentProvider(paymentLink.provider),
      url: /^(https?:\/\/|mailto:|tel:)/i.test(paymentLink.url)
        ? paymentLink.url
        : `https://${paymentLink.url}`
    }));

  return (
    <main style={{
      background:
        'linear-gradient(to right , #f9feff, #fdffff, #e5f4f9, #e8f5f8)'
    }}>
      {isLoading && (
        <div className="loader">
          <Preloader />
          <br />
        </div>
      )}
      {/* {error && (
        <MessageDisplay message={error} />
      )} */}
      {(user && !isLoading) && (
        <div>
          <div className="d-flex-center">
            <a href='https://takkasmart.com'>
              <img src={logoos} alt=""
                style={{ width: '50px', marginTop: '10px', marginBottom: '10px' }} /></a>



          </div>

          <div className="user-profile">
            <div className="user-profile-block">
              <div className="user-profile-banneros">
                <div className="user-profile-banner-wrapper">
                  <img
                    alt="Banner"
                    className="user-profile-banner-img"
                    src={user.banner}
                  />
                </div>
                <div className="user-profile-avatar-wrapperos">
                  <img
                    alt="Avatar"
                    className="user-profile-img"
                    src={user.avatar}
                  />
                </div>

              </div>






              <div className="user-profile-detailsos" >
                <h2 className="user-profile-name" style={{ fontSize: "2em" }}>{user.fullname}</h2>
                {!isPayment && user.job ?
                  <TypeAnimation
                    sequence={[`${user.job ? user.job : 'Welcome'}`, 3000]}
                    //  Continuing previous Text
                    style={{ fontSize: '1.3em' }}
                    wrapper="div"
                  />
                  : null}




                {isPayment ? (
                  <section className="payment-profile-section">
                    <h1 className="payment-profile-title">Payment Options</h1>
                    {paymentLinks.length > 0 ? (
                      <div className="payment-links-list">
                        {paymentLinks.map((paymentLink, index) => {
                          const paymentKey = `${paymentLink.provider}-${index}`;
                          const hasNumericLabel = isNumericPaymentLabel(paymentLink.label);

                          return (
                            <div className="payment-link-card" key={paymentKey}>
                              <a
                                className="payment-link-main"
                                href={paymentLink.url}
                                rel="noopener noreferrer"
                                target="_blank"
                              >
                                <img
                                  alt={`${paymentLink.providerDetails.label} logo`}
                                  className="payment-provider-logo"
                                  src={paymentLink.providerDetails.logo}
                                />
                                <span className="payment-provider-name">
                                  {paymentLink.providerDetails.label}
                                </span>
                              </a>
                              {paymentLink.label ? (
                                <div className="payment-label-row">
                                  <span className={`payment-link-label${hasNumericLabel
                                    ? ' payment-link-number'
                                    : ''}`}
                                  >
                                    {paymentLink.label}
                                  </span>
                                  {hasNumericLabel ? (
                                    <button
                                      aria-label={`Copy ${paymentLink.providerDetails.label} number`}
                                      className={`payment-copy-button${copiedPaymentLink === paymentKey
                                        ? ' payment-copy-button-copied'
                                        : ''}`}
                                      onClick={() => handleCopyPaymentNumber(
                                        paymentLink.label,
                                        paymentKey
                                      )}
                                      type="button"
                                    >
                                      {copiedPaymentLink === paymentKey
                                        ? <CheckOutlined />
                                        : <CopyOutlined />}
                                      {copiedPaymentLink === paymentKey ? 'Copied' : 'Copy'}
                                    </button>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-subtle">No payment links are available yet.</p>
                    )}
                  </section>
                ) : null}

                {!isPayment && user.bio ?

                  <div>
                    <hr style={{ borderTop: '0.2rem solid rgba(0, 0, 0, 0.1)' }} />
                    <div className="row justify-content-center">
                      <div className="d-flex flex-column align-items-center w-100">

                        <h1 style={{ fontSize: "1.5em", fontWeight: 'bold' }}>BIO</h1>

                        <h5 className="biobox">{user.bio}</h5>
                      </div>
                    </div>
                  </div> : null}

                {!isPayment && user.address ? (
                  <div className="profile-address-card">
                    <strong>Address</strong>
                    <span>{user.address}</span>
                  </div>
                ) : null}


                {isAllInOne && (user.mobile || user.bisMobile) ?

                  <div className="d-flex flex-column align-items-center">
                    <VCardFileCreator employee={user} className='name' />


                  </div> : null}
                {!isPayment && (user.email || user.mobile || user.bisMobile) ? <>
                <hr style={{ borderTop: '0.2rem solid rgba(0, 0, 0, 0.1)' }} />



                <h1 style={{ fontSize: "2em" }}>Contact Me</h1><br />



                <div className="">

                  {(user.mobile || user.bisMobile) ? (<div className="col-lg-4">

                    <div className="d-flex align-items-center">
                      <div className="icon-contact mb-3"><svg className="svg-inline--fa fa-mobile-alt fa-w-10"
                        aria-hidden="true" focusable="false" data-prefix="fas" data-icon="mobile-alt"
                        role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"
                        data-fa-i2svg="">
                        <path fill="currentColor"
                          d="M272 0H48C21.5 0 0 21.5 0 48v416c0 26.5 21.5 48 48 48h224c26.5 0 48-21.5 48-48V48c0-26.5-21.5-48-48-48zM160 480c-17.7 0-32-14.3-32-32s14.3-32 32-32 32 14.3 32 32-14.3 32-32 32zm112-108c0 6.6-5.4 12-12 12H60c-6.6 0-12-5.4-12-12V60c0-6.6 5.4-12 12-12h200c6.6 0 12 5.4 12 12v312z">
                        </path>
                      </svg>

                      </div>




                      <div className="font-weight-bold ml-3">
                        {user.mobile ?
                          <a href={`tel:${user.mobile}`}>{user.mobile}</a>
                          : null}

                        {user.bisMobile ? ' / ' : null}

                        {user.bisMobile ? <a href={`tel:${user.bisMobile}`}>
                          {user.bisMobile}</a> : null}



                      </div>
                    </div>
                  </div>

                  ) : null}

                  {/* {user.bisMobile ? (<div className="col-sm-4">
                    <div className="d-flex  align-items-center">
                      <div className="icon-contact mb-3"><svg className="svg-inline--fa fa-mobile-alt fa-w-10"
                        aria-hidden="true" focusable="false" data-prefix="fas" data-icon="mobile-alt"
                        role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"
                        data-fa-i2svg="">
                        <path fill="currentColor"
                          d="M272 0H48C21.5 0 0 21.5 0 48v416c0 26.5 21.5 48 48 48h224c26.5 0 48-21.5 48-48V48c0-26.5-21.5-48-48-48zM160 480c-17.7 0-32-14.3-32-32s14.3-32 32-32 32 14.3 32 32-14.3 32-32 32zm112-108c0 6.6-5.4 12-12 12H60c-6.6 0-12-5.4-12-12V60c0-6.6 5.4-12 12-12h200c6.6 0 12 5.4 12 12v312z">
                        </path>
                      </svg>
                      </div>


                      <div className="font-weight-bold"> {user.bisMobile}

                      </div>
                    </div>
                  </div>

                  ) : null } */}


                  {user.email ? (<div className="col-sm-4">
                    <hr style={{ borderTop: '0.2rem solid rgba(0, 0, 0, 0.1)' }} />

                    <div className="d-flex  align-items-center">
                      <div className="icon-contact mb-3"><svg className="svg-inline--fa fa-envelope fa-w-16"
                        aria-hidden="true" focusable="false" data-prefix="far" data-icon="envelope"
                        role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"
                        data-fa-i2svg="">
                        <path fill="currentColor"
                          d="M464 64H48C21.49 64 0 85.49 0 112v288c0 26.51 21.49 48 48 48h416c26.51 0 48-21.49 48-48V112c0-26.51-21.49-48-48-48zm0 48v40.805c-22.422 18.259-58.168 46.651-134.587 106.49-16.841 13.247-50.201 45.072-73.413 44.701-23.208.375-56.579-31.459-73.413-44.701C106.18 199.465 70.425 171.067 48 152.805V112h416zM48 400V214.398c22.914 18.251 55.409 43.862 104.938 82.646 21.857 17.205 60.134 55.186 103.062 54.955 42.717.231 80.509-37.199 103.053-54.947 49.528-38.783 82.032-64.401 104.947-82.653V400H48z">
                        </path>
                      </svg>
                      </div>
                      {/* <div className="text-muted">Email</div> */}
                      <a className="font-weight-bold ml-3"
                        href={`mailto:${user.email}`}
                        rel="noopener noreferrer"
                        target="_blank">{user.email}</a>
                    </div>
                  </div>) : null}

                </div>
                </> : null}
                {!isPayment && socialLinks.length > 0 ? (
                  <div>
                    <hr style={{ borderTop: '0.2rem solid rgba(0, 0, 0, 0.1)' }} />
                    <section className="my-links-section">
                      <h1 className='pb-4 fs-3'>My Links</h1>
                      <div className="social-media-list">
                        {socialLinks.map((account, index) => (
                          <div className="social-media-item p-2" key={`${account.url}-${index}`}>
                            {account.icon ? (
                              <a href={account.url} rel="noopener noreferrer" target="_blank">
                                <img
                                  alt={account.label || 'Custom link'}
                                  className="custom-link-icon"
                                  src={account.icon}
                                />
                              </a>
                            ) : (
                              <SocialIcon
                                network={account.network || undefined}
                                url={account.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ height: 60, width: 60 }}
                              />
                            )}
                            {account.label ? (
                              <span className={socialMediaLabelClassName}>
                                {account.label}
                              </span>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </section>
                  </div>
                ) : null}
                {isAllInOne && user.pdf ?

                  <div>
                    <div className="row justify-content-center">
                      <div className="d-flex flex-column align-items-center">

                        <h1 style={{ fontSize: "1.5em", fontWeight: 'bold' }}>Documents</h1>
                        <br />
                        <a href={user.pdf}>
                          <img src={adobePDFPNG} alt="" width="55" height="55" style={{ marginBottom: 10 }} /><br />
                          {user.pdfName}</a>
                      </div>
                    </div>
                  </div> : null}

                <a href='https://takkasmart.com'>

                  <h5 className="text-subtle text-italic">Copyright © @takka.smart</h5>
                </a>
              </div>
            </div>
          </div>







        </div>

      )}
    </main>
  );
};

export default ViewUser;



/*<div className="user-profile-banner">
                <div className="user-profile-banner-wrapper">
                  <ImageLoader
                    alt="Banner"
                    className="user-profile-banner-img"
                    src={user.banner}
                  />


                </div>
                <div>
                  <ImageLoader
                    alt="Avatar"
                    className="user-profile-img"
                    src={user.avatar}
                  />

                </div>
              </div>



            <div className="product-modal-details">
              <br />
              <span className="text-subtle">{user.email}</span>
              <h1 className="margin-top-0">{user.fullname}</h1>
              <span>{user.mobil}</span>
              <br />
              <br />
              <div className="divider" />
              <br />
              <div>
                <span className="text-subtle">Lens Width and Frame Size</span>
                <br />
                <br />

              </div>
              <br />

              <h1>{user.address}</h1>

            </div>*/
